/**
 * ============================================================================
 * BOOKING ENGINE REFERENCE — EXTRACTED FROM lanz-booking-engine.php v37.4
 * ============================================================================
 *
 * This file contains the critical JavaScript logic from the WordPress booking
 * engine that needs to be ported to the dispatch admin. Keep both systems in
 * sync — when the booking engine canonicalization changes, update here too.
 *
 * Full PHP lives in: /mnt/user-data/outputs/reference/lanz-booking-engine.php
 * (upload the PHP again when you need to see the rest — payment handlers,
 * email templates, congestion charge logic, etc.)
 *
 * ============================================================================
 * GOOGLE MAPS API KEY
 * ============================================================================
 */
export const GOOGLE_MAPS_API_KEY = 'AIzaSyDdXSRACDNrVvUMLci9WaWA-mUCrFxgarI';

/**
 * Loaded via:
 *   https://maps.googleapis.com/maps/api/js?key=${KEY}&libraries=places,geometry
 *
 * Restrictions to match booking engine behaviour:
 *   - componentRestrictions: { country: 'uk' }
 *   - types: ['establishment', 'geocode']
 *   - London bounds bias: (51.28,-0.50) to (51.60,0.20)
 */
export const GOOGLE_PLACES_OPTIONS = {
  types: ['establishment', 'geocode'],
  componentRestrictions: { country: 'gb' }, // 'gb' and 'uk' both accepted
};

export const LONDON_BOUNDS_BIAS = {
  sw: { lat: 51.28, lng: -0.50 },
  ne: { lat: 51.60, lng: 0.20 },
};

/**
 * ============================================================================
 * FBO BRANDS — Private jet terminals
 * ============================================================================
 * These are fixed-base operators at airports. Match by substring against
 * (place.name + ' || ' + place.formatted_address).toLowerCase().
 */
export const FBO_BRANDS = [
  { key: 'harrods aviation',    name: 'Harrods Aviation FBO' },
  { key: 'signature aviation',  name: 'Signature Aviation FBO' },
  { key: 'signature flight',    name: 'Signature Aviation FBO' },
  { key: 'universal aviation',  name: 'Universal Aviation FBO' },
  { key: 'jet centre',          name: 'Jet Centre FBO' },
  { key: 'jet center',          name: 'Jet Center FBO' },
  { key: 'jetex',               name: 'Jetex FBO' },
  { key: 'tag aviation',        name: 'TAG Aviation FBO' },
  { key: 'gama aviation',       name: 'Gama Aviation FBO' },
  { key: 'ocean sky',           name: 'Ocean Sky FBO' },
  { key: 'luxaviation',         name: 'Luxaviation FBO' },
];

/**
 * ============================================================================
 * KNOWN AIRPORTS — For attaching parent airport to FBO names
 * ============================================================================
 * When an FBO is detected, match against this list to format as:
 *   "Jetex FBO, Biggin Hill (BQH)"
 */
export const KNOWN_AIRPORTS = [
  { keys: ['heathrow'],                         display: 'Heathrow (LHR)' },
  { keys: ['gatwick'],                          display: 'Gatwick (LGW)' },
  { keys: ['luton'],                            display: 'Luton (LTN)' },
  { keys: ['stansted'],                         display: 'Stansted (STN)' },
  { keys: ['city airport', 'london city'],      display: 'City (LCY)' },
  { keys: ['farnborough'],                      display: 'Farnborough (FAB)' },
  { keys: ['biggin hill'],                      display: 'Biggin Hill (BQH)' },
  { keys: ['southampton'],                      display: 'Southampton (SOU)' },
  { keys: ['birmingham'],                       display: 'Birmingham (BHX)' },
  { keys: ['manchester'],                       display: 'Manchester (MAN)' },
  { keys: ['edinburgh'],                        display: 'Edinburgh (EDI)' },
  { keys: ['glasgow'],                          display: 'Glasgow (GLA)' },
  { keys: ['bristol'],                          display: 'Bristol (BRS)' },
  { keys: ['cardiff'],                          display: 'Cardiff (CWL)' },
  { keys: ['leeds'],                            display: 'Leeds Bradford (LBA)' },
  { keys: ['liverpool'],                        display: 'Liverpool (LPL)' },
  { keys: ['newcastle'],                        display: 'Newcastle (NCL)' },
  { keys: ['exeter'],                           display: 'Exeter (EXT)' },
  { keys: ['oxford'],                           display: 'Oxford (OXF)' },
  { keys: ['paris', 'le bourget'],              display: 'Paris Le Bourget (LBG)' },
  { keys: ['nice'],                             display: 'Nice (NCE)' },
  { keys: ['geneva'],                           display: 'Geneva (GVA)' },
  { keys: ['zurich'],                           display: 'Zurich (ZRH)' },
  { keys: ['frankfurt'],                        display: 'Frankfurt (FRA)' },
  { keys: ['munich'],                           display: 'Munich (MUC)' },
  { keys: ['amsterdam', 'schiphol'],            display: 'Amsterdam Schiphol (AMS)' },
  { keys: ['dublin'],                           display: 'Dublin (DUB)' },
  { keys: ['madrid'],                           display: 'Madrid (MAD)' },
  { keys: ['milan'],                            display: 'Milan (MXP)' },
  { keys: ['rome'],                             display: 'Rome (FCO)' },
  { keys: ['vienna'],                           display: 'Vienna (VIE)' },
];

/**
 * ============================================================================
 * CANONICAL PLACES — Airports and stations with locked-down display names
 * ============================================================================
 * These take precedence over Google's generic formatting. Airport entries have
 * isAirport:true so the "Terminal N" detection kicks in when the name/address
 * contains "terminal 1-5".
 *
 * Safety: airport entries only trigger when `placeSmellsLikeAirport` is true
 * (place has type=airport OR name contains airport/aviation/terminal/heliport/
 * aerodrome). Prevents "Signature Roofing, Luton" becoming "London Luton Airport".
 */
export const CANONICAL = [
  // Airports
  { keys: ['heathrow', 'lhr', 'tw6 '],                      canonical: 'London Heathrow Airport (LHR)',             isAirport: true },
  { keys: ['gatwick', 'lgw', 'rh6 '],                       canonical: 'London Gatwick Airport (LGW)',              isAirport: true },
  { keys: ['luton airport', 'ltn airport', 'lu2 9'],        canonical: 'London Luton Airport (LTN)',                isAirport: true },
  { keys: ['stansted', 'stn airport', 'cm24 1'],            canonical: 'London Stansted Airport (STN)',             isAirport: true },
  { keys: ['city airport', 'london city airport', 'lcy', 'e16 2'], canonical: 'London City Airport (LCY)',          isAirport: true },
  { keys: ['farnborough airport', 'fab', 'gu14 6'],         canonical: 'Farnborough Airport FBO (FAB)',             isAirport: true },
  { keys: ['biggin hill airport', 'bqh', 'tn16 3'],         canonical: 'London Biggin Hill Airport FBO (BQH)',      isAirport: true },
  // Stations
  { keys: ['st pancras', 'saint pancras', 'n1c 4'],         canonical: 'London St Pancras International Station' },
  { keys: ['king\u2019s cross', 'kings cross', "king's cross"], canonical: 'London King\u2019s Cross Station' },
  { keys: ['paddington station', 'london paddington'],      canonical: 'London Paddington Station' },
  { keys: ['victoria station', 'london victoria'],          canonical: 'London Victoria Station' },
  { keys: ['waterloo station', 'london waterloo'],          canonical: 'London Waterloo Station' },
  { keys: ['liverpool street'],                             canonical: 'London Liverpool Street Station' },
  { keys: ['euston station', 'london euston'],              canonical: 'London Euston Station' },
  { keys: ['marylebone station'],                           canonical: 'London Marylebone Station' },
  // Heliports (treated as airports — plane icon + same formatting)
  { keys: ['london heliport', 'battersea heliport', 'sw11 3bz'], canonical: 'London Heliport, Battersea (EGLW)',    isAirport: true },
  { keys: ['redhill aerodrome', 'rh1 5jy'],                 canonical: 'Redhill Aerodrome (KRH)',                   isAirport: true },
];

/**
 * ============================================================================
 * shortenAddress() — port of lanzShortenAddress() from booking engine
 * ============================================================================
 * Takes a Google Places Autocomplete result and returns a clean display string.
 *
 * Strategies:
 *   1. FBO detection (matches brand keywords + attaches parent airport)
 *   2. Canonical places (airports, stations, heliports)
 *   3. Other POIs (hotels, restaurants): "Name, City, Postcode"
 *   4. Plain street addresses: "Street, City Postcode"
 *
 * Also returns iconType so the caller can show airport/station/pin icon:
 *   - 'airport': airports, FBOs, heliports, aerodromes
 *   - 'station': train/transit stations, "station" in name, "pancras"
 *   - 'pin':     everything else (default)
 *
 * @param {google.maps.places.PlaceResult} place
 * @returns {{ display: string, iconType: 'airport'|'station'|'pin', isAirport: boolean }}
 */
export function shortenAddress(place) {
  if (!place) return { display: '', iconType: 'pin', isAirport: false };

  const types = place.types || [];
  const comps = place.address_components || [];
  const nameStr = (place.name || '').toLowerCase();
  const faStr = (place.formatted_address || '').toLowerCase();
  const combined = nameStr + ' || ' + faStr;

  function findComp(type) {
    return comps.find(c => c.types && c.types.includes(type)) || null;
  }

  const locality = findComp('postal_town') || findComp('locality');

  function getBestPostcode() {
    const pc = findComp('postal_code');
    const componentPC = pc ? pc.long_name : '';
    const fullPCRegex = /\b([A-Z]{1,2}[0-9][0-9A-Z]?)\s+([0-9][A-Z]{2})\b/i;
    const fa = place.formatted_address || '';
    const match = fa.match(fullPCRegex);
    if (match) {
      const fullPC = match[1].toUpperCase() + ' ' + match[2].toUpperCase();
      if (fullPC.length > componentPC.length) return fullPC;
    }
    return componentPC;
  }

  // Build result object with inferred icon
  function classifyIcon(display) {
    const lower = (display || '').toLowerCase();
    const isAirport = types.includes('airport')
      || lower.includes('airport')
      || lower.includes('aviation')
      || lower.includes('jet centre')
      || lower.includes('jet center')
      || lower.includes('heliport')
      || lower.includes('aerodrome')
      || /\((lhr|lgw|ltn|stn|lcy|fab|bqh|eglw|krh)\)/i.test(lower);
    const isStation = !isAirport && (
      types.includes('train_station')
      || types.includes('transit_station')
      || types.includes('subway_station')
      || lower.includes('station')
      || lower.includes('pancras')
    );
    return {
      iconType: isAirport ? 'airport' : isStation ? 'station' : 'pin',
      isAirport,
    };
  }

  // 1. FBO detection — run FIRST so FBOs don't get overwritten by airport canonical
  for (const fbo of FBO_BRANDS) {
    if (combined.includes(fbo.key)) {
      let parentAirport = '';
      for (const ap of KNOWN_AIRPORTS) {
        if (ap.keys.some(k => combined.includes(k))) {
          parentAirport = ap.display;
          break;
        }
      }
      if (!parentAirport) {
        const locality2 = findComp('postal_town') || findComp('locality') || findComp('administrative_area_level_2');
        if (locality2) parentAirport = locality2.long_name;
      }
      const display = parentAirport ? `${fbo.name}, ${parentAirport}` : fbo.name;
      return { display, ...classifyIcon(display) };
    }
  }

  // 2. Canonical places — only apply airport canonical when place genuinely looks airport-y
  const placeSmellsLikeAirport = types.includes('airport')
    || nameStr.includes('airport')
    || nameStr.includes('aviation')
    || nameStr.includes('terminal')
    || nameStr.includes('heliport')
    || nameStr.includes('aerodrome')
    || faStr.includes(' airport')
    || faStr.includes('airport,')
    || faStr.includes('heliport')
    || faStr.includes('aerodrome');

  for (const entry of CANONICAL) {
    if (entry.isAirport && !placeSmellsLikeAirport) continue;
    for (const key of entry.keys) {
      if (combined.includes(key)) {
        if (entry.isAirport) {
          const termMatch = combined.match(/terminal\s*([1-5])/);
          if (termMatch) {
            const iataMatch = entry.canonical.match(/ \([A-Z]{3}\)$/);
            if (iataMatch) {
              const display = entry.canonical.replace(iataMatch[0], '') + ' Terminal ' + termMatch[1] + iataMatch[0];
              return { display, ...classifyIcon(display) };
            }
          }
        }
        return { display: entry.canonical, ...classifyIcon(entry.canonical) };
      }
    }
  }

  // 3. Google-classified airport/station not in our canonical list
  const isAirportOrStation = types.includes('airport')
    || types.includes('train_station')
    || types.includes('transit_station')
    || types.includes('subway_station');
  if (isAirportOrStation && place.name) {
    return { display: place.name, ...classifyIcon(place.name) };
  }

  // 4. Other POIs (hotels, restaurants, etc.)
  const isOtherPOI = !isAirportOrStation && (
    types.includes('establishment')
    || types.includes('point_of_interest')
    || types.includes('lodging')
  );
  if (isOtherPOI && place.name) {
    const postcodeOther = getBestPostcode();
    const pieces = [place.name];
    if (locality && !place.name.toLowerCase().includes(locality.long_name.toLowerCase())) {
      pieces.push(locality.long_name);
    }
    if (postcodeOther) {
      if (pieces.length > 1) {
        pieces[pieces.length - 1] = pieces[pieces.length - 1] + ' ' + postcodeOther;
      } else {
        pieces.push(postcodeOther);
      }
    }
    const display = pieces.join(', ');
    return { display, ...classifyIcon(display) };
  }

  // 5. Plain street address
  const streetNum = findComp('street_number');
  const route = findComp('route');
  const postcode = getBestPostcode();
  const parts = [];
  if (streetNum && route) {
    parts.push(streetNum.long_name + ' ' + route.long_name);
  } else if (route) {
    parts.push(route.long_name);
  } else if (place.name) {
    parts.push(place.name);
  }
  if (locality && !parts.includes(locality.long_name)) {
    parts.push(locality.long_name);
  }
  if (postcode) {
    if (parts.length > 0) {
      parts[parts.length - 1] = parts[parts.length - 1] + ' ' + postcode;
    } else {
      parts.push(postcode);
    }
  }
  let display = parts.join(', ');
  if (!display && place.formatted_address) {
    display = place.formatted_address.replace(/,\s*(United Kingdom|UK)\s*$/i, '');
  }
  return { display, ...classifyIcon(display) };
}

/**
 * ============================================================================
 * extractPostcode() — pull full UK postcode from a display string
 * ============================================================================
 * Used when saving an address manually (no Google Place result) so we still
 * populate the separate postcode column for geocoding/search.
 */
export function extractPostcode(addressStr) {
  if (!addressStr) return null;
  const match = addressStr.match(/\b([A-Z]{1,2}[0-9][0-9A-Z]?)\s+([0-9][A-Z]{2})\b/i);
  if (!match) return null;
  return match[1].toUpperCase() + ' ' + match[2].toUpperCase();
}

/**
 * ============================================================================
 * classifyAddressIcon() — infer icon from a plain string (no Google Place)
 * ============================================================================
 * When restoring saved addresses from the database, we don't have the Google
 * Place object anymore — just the canonical string. Use this to pick an icon.
 */
export function classifyAddressIcon(addressStr) {
  if (!addressStr || !addressStr.trim()) return 'pin';
  const a = addressStr.toLowerCase();
  if (a.includes('airport')
    || a.includes('aviation')
    || a.includes('heliport')
    || a.includes('aerodrome')
    || /\((lhr|lgw|ltn|stn|lcy|fab|bqh|eglw|krh)\)/i.test(a)) {
    return 'airport';
  }
  if (a.includes('station') || a.includes('pancras')) {
    return 'station';
  }
  return 'pin';
}
